import React, { useState } from 'react';
import { SubmitEstimateData } from '../../hooks/useTaskEstimates';
import './EstimateSubmitModal.css';

interface EstimateSubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SubmitEstimateData) => Promise<{ success: boolean; message?: string }>;
  
  // Pre-filled data from task creation form
  taskDescription: string;
  taskType: string;
  projectId?: string;
  projectName?: string;
  accountId?: string;
  accountName?: string;
  employeeId: string;
  employeeName: string;
  mondayTaskId?: string;
}

const EstimateSubmitModal: React.FC<EstimateSubmitModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  taskDescription,
  taskType,
  projectId,
  projectName,
  accountId,
  accountName,
  employeeId,
  employeeName,
  mondayTaskId
}) => {
  const [estimateHours, setEstimateHours] = useState<string>('');
  const [estimateNotes, setEstimateNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate employee is logged in
    if (!employeeId) {
      setError('Not logged in. Please refresh the page and try again.');
      return;
    }

    const hours = parseFloat(estimateHours);
    if (isNaN(hours) || hours <= 0) {
      setError('Please enter a valid estimate (greater than 0 hours)');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await onSubmit({
        taskDescription,
        taskType,
        projectId,
        projectName,
        accountId,
        accountName,
        employeeId,
        employeeName,
        estimateHours: hours,
        estimateNotes: estimateNotes.trim() || undefined,
        mondayTaskId
      });

      if (result.success) {
        setSuccess(true);
        // Auto-close after success message
        setTimeout(() => {
          onClose();
          setEstimateHours('');
          setEstimateNotes('');
          setSuccess(false);
        }, 2000);
      } else {
        setError(result.message || 'Failed to submit estimate');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEstimateHours('');
      setEstimateNotes('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <div className="estimate-modal-overlay" onClick={handleClose}>
      <div className="estimate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="estimate-modal-header">
          <h3>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Submit Time Estimate
          </h3>
          <button className="modal-close" onClick={handleClose} disabled={isSubmitting}>×</button>
        </div>

        {success ? (
          <div className="estimate-modal-success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <h4>Estimate Submitted!</h4>
            <p>Your estimate has been sent for approval. You can start working now, but cannot exceed 1 hour without approval.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="estimate-modal-body">
              <div className="estimate-task-preview">
                <div className="preview-label">Task</div>
                <div className="preview-value">{taskDescription}</div>
                
                <div className="preview-row">
                  <div className="preview-item">
                    <span className="preview-label">Type</span>
                    <span className="preview-badge">{taskType}</span>
                  </div>
                  {(projectName || accountName) && (
                    <div className="preview-item">
                      <span className="preview-label">Project/Account</span>
                      <span className="preview-value-small">{projectName || accountName}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="estimate-form-group">
                <label htmlFor="estimateHours">
                  Time Estimate (hours) <span className="required">*</span>
                </label>
                <input
                  id="estimateHours"
                  type="number"
                  step="0.25"
                  min="0.25"
                  max="100"
                  value={estimateHours}
                  onChange={(e) => setEstimateHours(e.target.value)}
                  placeholder="e.g., 2"
                  className="estimate-input"
                  autoFocus
                  disabled={isSubmitting}
                />
                <span className="estimate-hint">
                  How many hours do you expect this task to take?
                </span>
              </div>

              <div className="estimate-form-group">
                <label htmlFor="estimateNotes">Notes (optional)</label>
                <textarea
                  id="estimateNotes"
                  value={estimateNotes}
                  onChange={(e) => setEstimateNotes(e.target.value)}
                  placeholder="Any context about your estimate (e.g., assumptions, dependencies, risks)"
                  className="estimate-textarea"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <div className="estimate-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {error}
                </div>
              )}

              <div className="estimate-info-box">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <div>
                  <strong>Start Work Immediately</strong>
                  <p>
                    Your estimate will be sent to Managers for approval. 
                    You can start working right away, but cannot exceed 1 hour without approval.
                    You'll receive a notification when the estimate is approved or if feedback is provided.
                  </p>
                </div>
              </div>
            </div>

            <div className="estimate-modal-footer">
              <button 
                type="button" 
                className="estimate-btn-cancel" 
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="estimate-btn-submit"
                disabled={isSubmitting || !estimateHours}
                title={!estimateHours ? 'Enter time estimate first' : 'Submit estimate for approval'}
              >
                {isSubmitting ? (
                  <>
                    <div className="btn-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                    {!estimateHours ? 'Enter Estimate First' : 'Submit for Approval'}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EstimateSubmitModal;

